"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { UserRecord } from "@huub-web/ui";
import type {
  NavigationState,
  AppCustomer,
  AppCurrent,
  AppStateResult,
} from "@huub-web/ui";
import { useDelayedState, useDebug } from "@huub-web/ui";
import { isObject } from "@huub-web/utils";

import { useAuth } from "./useAuth";
import { useAPI } from "./api/useAPI";
import {
  type WebSocketNavigationStateMessage,
  WebSocketChannel,
} from "../types";
import { useWebSocketListener } from "./websocket/useWebSocketListener";

export type AppContext = {
  navigationState: NavigationState;
  appCustomer: AppCustomer | null;
  appCurrent: AppCurrent | null;
  /**
   * Sorted list of enabled application feature flag keys, or ``null`` when
   * the backend did not send the field (older backends, or before the first
   * fetch lands). Consumers should treat ``null`` as "no opinion" and
   * default to showing flag-gated UI, but treat an empty array as
   * authoritative "no flags enabled" and hide flag-gated UI.
   */
  enabledAppFlags: string[] | null;
  user: AppUser;
  isAppStateLoading: boolean;
  errorLoadingAppState: string | null;
};

export type AppUser = UserRecord & {
  email: string;
};

/**
 * Fetches the navigation state from the backend and listens for updates via the websocket.
 * @deprecated This hook is should not be used other then in AppController. Use useAppController if needed elsewhere.
 */
export const useApp = (): AppContext => {
  const { $get } = useAPI();
  const { t } = useTranslation();
  const { log } = useDebug();
  const { identityToken, indentity } = useAuth();

  const [navigationState, setNavigationState] = useState<NavigationState>([]);
  const [appCustomer, setAppCustomer] = useState<AppCustomer | null>(null);
  const [appCurrent, setAppCurrent] = useState<AppCurrent | null>(null);
  const [enabledAppFlags, setEnabledAppFlags] = useState<string[] | null>(null);

  const [errorLoadingAppState, setError] = useState<string | null>(null);
  const [isAppStateLoading, setLoading] = useDelayedState<boolean>(true, 500);
  const isLoadingRef = useRef<boolean>(false);

  const user: AppUser = (() => {
    if (appCustomer) {
      if (appCustomer.firstName === "Voornaam") {
        return {
          name: appCustomer.email ?? "",
          email: appCustomer.email ?? "",
        };
      }

      return {
        name: `${appCustomer.firstName ?? ""} ${appCustomer.middleName ?? ""} ${appCustomer.lastName ?? ""}`,
        email: appCustomer.email ?? "",
      };
    }
    return {
      name: indentity?.name ?? indentity?.email ?? "",
      email: indentity?.email ?? "",
    };
  })();

  // TODO: Remove, this is a temporary fix for the nav state until the backend is updated
  const fixNavigationState = (navigationState: NavigationState) => {
    const updatedNavigationState = [...navigationState];

    // Add house at the end if not present
    const orientationStep = updatedNavigationState.find(
      (step) => step.id === "orientation",
    );

    if (orientationStep) {
      const houseRecord = orientationStep.steps?.find(
        (step) => step.id === "[id]",
      );
      if (!houseRecord) {
        console.warn(
          "[useAppState] House record not found in orientation navigation state, adding it",
        );

        if (!orientationStep.steps) {
          orientationStep.steps = [];
        }

        orientationStep.steps?.push({
          id: "[id]",
          title: "[title]",
          slug: "[id]",
          hidden: true,
          endpoint: "/frontend/orientation/[id]",
        });
      }
    } else {
      console.warn("[useAppState] Orientation not found in navigation state");
    }

    // Add advisor at the end if not present
    if (!updatedNavigationState.find((step) => step.id === "advisor")) {
      console.warn(
        "[useAppState] Advisor not found in navigation state, adding it",
      );
      updatedNavigationState.push({
        id: "advisor",
        title: t("advisor.meet.title"),
        slug: "adviseur-spreken",
        hidden: true,
      });
    }

    // Add chat history
    if (!updatedNavigationState.find((step) => step.id === "chatHistory")) {
      updatedNavigationState.push({
        id: "chatHistory",
        title: t("history.title"),
        slug: "gesprekshistorie",
        hidden: true,
        disabled: true,
        steps: [
          {
            id: "[id]",
            title: t("history.chat.title"),
            slug: "[id]",
            hidden: true,
          },
        ],
      });
    }

    return updatedNavigationState;
  };

  // At app start, fetch the navigation state via API
  // ------------------------------------------

  useEffect(() => {
    if (!identityToken || navigationState.length > 0 || isLoadingRef.current) {
      return;
    }

    log("orange", "useAppState", "Fetching navigation state");

    const fetchData = async () => {
      if (isLoadingRef.current) {
        return;
      }

      setError(null);
      setLoading(true);
      isLoadingRef.current = true;

      try {
        const response = await $get("/frontend/state");
        if (response?.data && isObject<AppStateResult>(response.data)) {
          setNavigationState(fixNavigationState(response.data.state));
          setAppCustomer(response.data.customer);
          setAppCurrent(response.data.current);
          setEnabledAppFlags(
            Array.isArray(response.data.enabledAppFlags)
              ? response.data.enabledAppFlags
              : null,
          );
        } else {
          setError("Invalid response");
          console.warn(
            "[useAppState] Received invalid navigation state update via API",
            response,
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError(String(err));
        }
      } finally {
        isLoadingRef.current = false;
        setLoading(false);
      }
    };

    fetchData();
  }, [identityToken, navigationState.length]);

  // Also listen to updates via the websocket
  // ----------------------------------------

  useWebSocketListener<WebSocketNavigationStateMessage>(
    WebSocketChannel.NAVIGATION_STATE,
    (message) => {
      if (!isObject(message.data) || !message?.data?.state) {
        console.warn(
          "[useAppState] Received invalid navigation state update via websocket",
          message,
        );
        return;
      }

      setNavigationState(fixNavigationState(message.data.state));
      setAppCustomer(message.data.customer);
      setAppCurrent(message.data.current);
      if (Array.isArray(message.data.enabledAppFlags)) {
        setEnabledAppFlags(message.data.enabledAppFlags);
      }
    },
  );

  return {
    navigationState,
    appCustomer,
    appCurrent,
    enabledAppFlags,
    user,
    isAppStateLoading,
    errorLoadingAppState,
  };
};
